import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { OperacionLCSLHPTITablaValorVista, OperacionLCSLHPTITablaValorVistaInicial } from '../types/dto';
import { OperacionLCSLHPTITablaValorFields } from '../types/fields';
import { HttpLCSLHPTI } from '../api/httpLCSLSHPTI';

export interface LCSLHPTIHeader {
  codOperacionTipo: number;
  descOperacionTipo: string;
}

export interface LCSLHPTIGroupedHeader {
  agrupacion: string;
  columnas: LCSLHPTIHeader[];
}

export interface LCSLHPTIRow {
  rowKey: string | number;
  codInstrumento: number;
  descInstrumento: string;
  orden: number;
  permiteEditarInstrumento: boolean;
  celdas: Record<number, OperacionLCSLHPTITablaValorVistaInicial | OperacionLCSLHPTITablaValorVista>;
}

export const pivotLCSLHPTIData = (flatData: any[]) => {
  if (!flatData || !flatData.length) return { groupedHeaders: [], rows: [] };

  const headersMap = new Map<number, LCSLHPTIHeader & { agrupacion: string }>();
  const rowsMap = new Map<number, LCSLHPTIRow>();

  flatData.forEach((item) => {
    const codTipo = item[OperacionLCSLHPTITablaValorFields.CodOperacionTipo];
    const codInst = item[OperacionLCSLHPTITablaValorFields.CodInstrumento];
    const descInst = item[OperacionLCSLHPTITablaValorFields.DescInstrumento];
    // rowId se settea en las filas de "Otros" al agregarlas, en el componente donde vive la tabla. Para las de la api es undefined asi que equeda el codigo del instrumento
    const rowKey = item.rowId || (codInst === 15 ? `${codInst}-${descInst}` : codInst);

    if (!headersMap.has(codTipo)) {
      headersMap.set(codTipo, {
        codOperacionTipo: codTipo,
        descOperacionTipo: item[OperacionLCSLHPTITablaValorFields.DescOperacionTipo],
        agrupacion: item[OperacionLCSLHPTITablaValorFields.Agrupacion]
      });
    }

    if (!rowsMap.has(rowKey)) {
      rowsMap.set(rowKey, {
        rowKey: rowKey,
        codInstrumento: codInst,
        descInstrumento: item[OperacionLCSLHPTITablaValorFields.DescInstrumento] as string,
        orden: (item[OperacionLCSLHPTITablaValorFields.Orden] as number) ?? 0,
        permiteEditarInstrumento: !!item[OperacionLCSLHPTITablaValorFields.PermiteEditarInstrumento],
        celdas: {}
      });
    }

    const row = rowsMap.get(rowKey)!;
    row.celdas[codTipo] = item;
  });

  const groupedHeadersMap = new Map<string, LCSLHPTIGroupedHeader>();
  headersMap.forEach((header) => {
    if (!groupedHeadersMap.has(header.agrupacion)) {
      groupedHeadersMap.set(header.agrupacion, { agrupacion: header.agrupacion, columnas: [] });
    }
    groupedHeadersMap.get(header.agrupacion)!.columnas.push({
      codOperacionTipo: header.codOperacionTipo,
      descOperacionTipo: header.descOperacionTipo
    });
  });

  const groupedHeaders = Array.from(groupedHeadersMap.values());
  const rows = Array.from(rowsMap.values()).sort((a, b) => a.orden - b.orden);

  return { groupedHeaders, rows };
};

export const useLCSLHPTITablaValorInicial = (sinCodPais: number | undefined | null) => {
  const {
    data: tablaValorOriginal = [],
    isLoading,
    isError,
    error
  } = useQuery<OperacionLCSLHPTITablaValorVistaInicial[], Error>({
    queryKey: ['LCSLHPTI-tabla-valor-inicial', sinCodPais],
    queryFn: () => HttpLCSLHPTI.getTablaValoresInicial(sinCodPais!),
    enabled: !!sinCodPais
  });

  const tablaValorFiltrada = useMemo(() => {
    return tablaValorOriginal.filter(item =>
      item[OperacionLCSLHPTITablaValorFields.CodInstrumento] !== 15
    );
  }, [tablaValorOriginal]);


  const pivotData = useMemo(() => pivotLCSLHPTIData(tablaValorFiltrada), [tablaValorFiltrada]);

  return {
    groupedHeaders: pivotData.groupedHeaders,
    rows: pivotData.rows,
    valores: tablaValorFiltrada,
    isLoading,
    isError,
    error
  };
};


export const useLCSLHPTITablaValor = (idOperacion: number | undefined | null) => {
  const {
    data: tablaValorData = [],
    isLoading,
    isError,
    error
  } = useQuery<OperacionLCSLHPTITablaValorVista[], Error>({
    queryKey: ['LCSLHPTI-tabla-valor', idOperacion],
    queryFn: () => HttpLCSLHPTI.getTablaValores(idOperacion!),
    enabled: !!idOperacion
  });

  const pivotData = useMemo(() => pivotLCSLHPTIData(tablaValorData), [tablaValorData]);

  return {
    groupedHeaders: pivotData.groupedHeaders,
    rows: pivotData.rows,
    valores: tablaValorData,
    isLoading,
    isError,
    error
  };
};